import { SplashScreen } from "@capacitor/splash-screen";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { useNavigate, useRouteError } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { TextButton } from "atoms/text-button/TextButton";
import useHistory from "hooks/useHistory";
import { useLogout } from "utils/useLogout";

const RootError = () => {
  const error = useRouteError();
  const { routeBackNumber } = useHistory();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const logout = useLogout();

  useEffect(() => {
    SplashScreen.hide();
  }, []);

  useEffect(() => {
    if (isAxiosError(error) && error.response && error.request) {
      console.error(
        `The request to ${error.request.responseURL} failed with error code ${error.response.status}`,
        error.toJSON(),
      );
    } else {
      console.error("Page error occurred:", error);
    }
  }, [error]);

  function handleGoBack() {
    if (window.history.length > 1) {
      navigate(routeBackNumber);
    } else {
      navigate("/home");
    }
  }

  async function handleLogout() {
    await logout();
    location.reload();
  }

  function handleContactSupport() {
    location.href = "mailto:support@repeatmd.com";
  }

  return (
    <>
      <div className="flex h-screen flex-auto flex-col items-center justify-center bg-gradient-to-b from-white to-gray-50 px-6 text-center">
        <div className="mx-auto w-full max-w-md">
          <div
            className="mb-6 flex items-center justify-center"
            role="img"
            aria-label={t("rootError.imageAriaLabel")}
          >
            <img
              src="/assets/error-500-mona.png"
              alt={t("rootError.imageAlt")}
              className="mx-auto block h-48 w-48 rounded-full object-cover object-[50%_30%]"
            />
          </div>

          <h1 className="text-ink-dark text-h1">{t("rootError.title")}</h1>

          <p className="text-ink-dark mt-2 text-bold1">{t("rootError.subtitle")}</p>
          <p className="text-ink-secondary mt-1 text-body2">{t("rootError.body")}</p>

          <div className="mt-6 grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
            <TextButton
              className="!rounded-full !border-0 !bg-black !text-white !shadow sm:col-span-2"
              onClick={handleGoBack}
            >
              {t("rootError.goBack")}
            </TextButton>
            <TextButton
              className="!rounded-full !border !border-gray-200 !bg-white !text-primary sm:col-span-2"
              onClick={handleLogout}
            >
              {t("rootError.logout")}
            </TextButton>
          </div>

          {error instanceof Error && import.meta.env.REACT_APP_ENV === "dev" && (
            <div className="mt-6 space-y-2 text-center">
              <h2 className="text-red-500 text-xl font-semibold">{error.name}</h2>
              <h3 className="text-red-400 text-lg font-medium">{error.message}</h3>
              <pre className="text-red-300 text-sm">{error.stack}</pre>
            </div>
          )}
          <TextButton
            className="mt-4"
            onClick={handleContactSupport}
            data-testid="root-error-contact-support-button"
          >
            {t("rootError.contactSupport")}
          </TextButton>
        </div>
      </div>
    </>
  );
};

export default RootError;
